import * as React from 'react';

import { useQuery } from '@apollo/client';
import OptimizationParentJobsQuery, {
  OptimizationParentJobsQueryVariables,
  OptimizationParentJobsResponse,
} from '@graphql/queries/OptimizationParentJobsQuery';
import ReportJobQuery from '@graphql/queries/ReportJobQuery';
import {
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { TextField } from '@mui/material';
import { Pagination } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import sessionTokenRoles from 'selectors/sessionTokenRoles';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import { formatDate } from 'utils/dateTime';

import { constructArtifactPath } from '../../../utils/conchUtils';
import { hasSuperAdminRole } from '../../../utils/roleUtils';

export const generateArtifactPath = (
  record: ReportJobData,
  efsProjectName: string,
) => {
  return constructArtifactPath(
    record.startedAt,
    record.clientName,
    efsProjectName ?? record.projectName,
    record.targetGroupName,
  );
};

export interface ReportJobData {
  jobId: string;
  startedAt: string;
  reportType: string;
  reportId: string;
  targetGroupName: string;
  clientName: string;
  projectName: string;
  workspaceId: string;
  artifact_path: string;
}

interface ReportJobsResponse {
  allReportJobs: {
    nodes: Array<ReportJobData>;
  };
}

interface ReportJobsQueryVariables {
  workspaceID: string;
  notReportType: 'market_survey' | 'optimization';
  passedQA?: boolean;
  orderBy: string;
}

type AutocompleteOption = {
  id: number;
  name: string;
  value: number;
};

interface Props {
  selectReport: (report: ReportJobData) => void;
  resetFields: () => void;
  initialSelectedReport?: ReportJobData;
  onlyLinkedOptimizationReports?: boolean;
  initialFilterClientName?: AutocompleteOption | null;
  initialFilterProjectName?: AutocompleteOption | null;
  initialFilterReportId?: AutocompleteOption | null;
  initialFilterPassedQA?: boolean;
  onFilterChange?: (field: string, value: any) => void;
}

const isAutoCompleteOption = (obj: any): obj is AutocompleteOption =>
  obj &&
  typeof obj.id === 'number' &&
  typeof obj.name === 'string' &&
  typeof obj.value === 'number';

const SelectReport: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const { onlyLinkedOptimizationReports = false, onFilterChange } = props;

  const workspaceID = useSelector((state) => selectWorkspaceProducerId(state));

  const userRoles = useSelector((state) => sessionTokenRoles(state));
  const isSuperAdmin = hasSuperAdminRole(userRoles);

  const [passedQA, setPassedQA] = React.useState<boolean | undefined>(props.initialFilterPassedQA ?? true);
  const [selectedReport, setSelectedReport] = React.useState<ReportJobData>(props.initialSelectedReport);
  const [reportJobs, setReportJobs] = React.useState<Array<ReportJobData>>([]);
  const [filteredReportJobs, setFilteredReportJobs] = React.useState<
    Array<ReportJobData>
  >([]);
  const [page, setPage] = React.useState<number>(1);
  const [filterClientName, setFilterClientName] = React.useState<AutocompleteOption | null>(props.initialFilterClientName ?? null);
  const [filterProjectName, setFilterProjectName] = React.useState<AutocompleteOption | null>(props.initialFilterProjectName ?? null);
  const [filterReportId, setFilterReportId] = React.useState<AutocompleteOption | null>(props.initialFilterReportId ?? null);

  const applyFilters = React.useCallback(
    (jobs: Array<ReportJobData>) => {
      return jobs.filter((job) => {
        if (filterClientName && job.clientName !== filterClientName.name) {
          return false;
        }

        if (filterProjectName && job.projectName !== filterProjectName.name) {
          return false;
        }

        if (filterReportId && job.reportId !== filterReportId.name) {
          return false;
        }

        return true;
      });
    },
    [filterClientName, filterProjectName, filterReportId],
  );

  React.useEffect(() => {
    setFilteredReportJobs(applyFilters(reportJobs));
    setPage(1);
  }, [applyFilters, reportJobs]);
  const { data: reportJobsData, loading } = useQuery<
    ReportJobsResponse,
    ReportJobsQueryVariables
  >(ReportJobQuery, {
    variables: {
      notReportType: 'optimization',
      ...(passedQA === true && { passedQA: true }),
      orderBy: 'STARTED_AT_DESC',
      ...(!isSuperAdmin && { workspaceID: workspaceID }),
    },
  });

  const { data: optimizationParentJobsData, loading: optimizationParentJobsLoading } =
    useQuery<
      OptimizationParentJobsResponse,
      OptimizationParentJobsQueryVariables
    >(OptimizationParentJobsQuery, {
      skip: !onlyLinkedOptimizationReports,
      variables: {
        orderBy: 'STARTED_AT_DESC',
        ...(!isSuperAdmin && { workspaceID: workspaceID }),
      },
    });

  React.useEffect(() => {
    if (!reportJobsData) {
      return;
    }

    if (!onlyLinkedOptimizationReports) {
      setReportJobs(reportJobsData.allReportJobs.nodes);
      setFilteredReportJobs(reportJobsData.allReportJobs.nodes);
      setPage(1);
      return;
    }

    if (!optimizationParentJobsData) {
      return;
    }

    const optimizationParentJobIds = new Set(
      optimizationParentJobsData.allReportJobs.nodes.filter(Boolean)
        .map((job) => job.parentJobId)
        .filter(Boolean),
    );

    const eligibleReports = reportJobsData.allReportJobs.nodes.filter((job) =>
      optimizationParentJobIds.has(job.jobId),
    );

    setReportJobs(eligibleReports);
    setFilteredReportJobs(eligibleReports);
    setPage(1);
  }, [reportJobsData, optimizationParentJobsData, onlyLinkedOptimizationReports]);

  if (loading || (onlyLinkedOptimizationReports && optimizationParentJobsLoading) || reportJobs.length == 0) {
    return <div />;
  }

  return (
    <Container>
      <Grid
        key={selectedReport?.reportId}
        container
        spacing={3}
        style={{ display: 'flex', flexDirection: 'row' }}
      >
        <Grid container spacing={1}>
          <Grid
            item
            xs={11}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              padding: '2em',
            }}
          >
            <Autocomplete
              id="client-name-field-autocomplete"
              value={filterClientName}
              getOptionLabel={(option: {
                id: number;
                name: string;
                value: number;
              }) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={_.chain(reportJobs)
                .uniqBy('clientName')
                .map(
                  (job, index): AutocompleteOption => ({
                    id: index,
                    name: job.clientName,
                    value: index,
                  }),
                )
                .value()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reports.request.clientName')}
                  variant="outlined"
                  style={{ minWidth: 250 }}
                />
              )}
              onChange={(_event, value) => {
                setFilterClientName(isAutoCompleteOption(value) ? value : null);
                onFilterChange?.('filter_client_name', isAutoCompleteOption(value) ? value : null);
                setFilteredReportJobs(
                  reportJobs.filter((job) =>
                    job.clientName
                      .toLowerCase()
                      .includes(
                        (isAutoCompleteOption(value)
                          ? value.name
                          : ''
                        ).toLowerCase(),
                      ),
                  ),
                );
              }}
            />
            <Autocomplete
              id="project-name-field-autocomplete"
              value={filterProjectName}
              getOptionLabel={(option: {
                id: number;
                name: string;
                value: number;
              }) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={_.chain(reportJobs)
                .uniqBy('projectName')
                .map(
                  (job, index): AutocompleteOption => ({
                    id: index,
                    name: job.projectName,
                    value: index,
                  }),
                )
                .value()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reports.request.projectName')}
                  variant="outlined"
                  style={{ minWidth: 250 }}
                />
              )}
              onChange={(_event, value) => {
                setFilterProjectName(isAutoCompleteOption(value) ? value : null);
                onFilterChange?.('filter_project_name', isAutoCompleteOption(value) ? value : null);
                setFilteredReportJobs(
                  reportJobs.filter((job) =>
                    job.projectName
                      .toLowerCase()
                      .includes(
                        (isAutoCompleteOption(value)
                          ? value.name
                          : ''
                        ).toLowerCase(),
                      ),
                  ),
                );
              }}
            />
            <Autocomplete
              id="reportid-field-autocomplete"
              value={filterReportId}
              getOptionLabel={(option: {
                id: number;
                name: string;
                value: number;
              }) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={_.chain(reportJobs)
                .uniqBy('reportId')
                .map(
                  (job, index): AutocompleteOption => ({
                    id: index,
                    name: job.reportId,
                    value: index,
                  }),
                )
                .value()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reports.request.reportId')}
                  variant="outlined"
                  style={{ minWidth: 250 }}
                />
              )}
              onChange={(_event, value) => {
                setFilterReportId(isAutoCompleteOption(value) ? value : null);
                onFilterChange?.('filter_report_id', isAutoCompleteOption(value) ? value : null);
                setFilteredReportJobs(
                  reportJobs.filter((job) =>
                    job.reportId
                      .toLowerCase()
                      .includes(
                        (isAutoCompleteOption(value)
                          ? value.name
                          : ''
                        ).toLowerCase(),
                      ),
                  ),
                );
              }}
            />
          </Grid>
          <Grid
            item
            xs={1}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-around',
              padding: '2em',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={passedQA === true}
                  onChange={(_event, value) => {
                    setSelectedReport(null);
                    const newPassedQA = value ? true : undefined;
                    setPassedQA(newPassedQA);
                    onFilterChange?.('filter_passed_qa', newPassedQA);
                    setPage(1);
                  }}
                />
              }
              label={t('reports.request.passedQa')}
            />
          </Grid>
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.request.selectReport')}</TableCell>
                  <TableCell>{t('reports.request.clientName')}</TableCell>
                  <TableCell>{t('reports.request.projectName')}</TableCell>
                  <TableCell>{t('reports.request.targetGroupName')}</TableCell>
                  <TableCell>{t('reports.request.startedAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReportJobs
                  .filter((job) => !!job.jobId && job.jobId.trim() !== '')//THis IS Adding the check to avoid archtype projects while listing
                  .slice((page - 1) * 5, page * 5)
                  .map((job) => (
                    <TableRow key={job.reportId}>
                      <TableCell>
                        <Tooltip title="Select report." arrow>
                          <Checkbox
                            color="secondary"
                            checked={job.reportId == selectedReport?.reportId}
                            onChange={(_event, _checked) => {
                              setSelectedReport(job);
                              props.selectReport(job);
                              props.resetFields();
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>{job.clientName}</TableCell>
                      <TableCell>{job.projectName}</TableCell>
                      <TableCell>{job.targetGroupName}</TableCell>
                      <TableCell>
                        {formatDate(job.startedAt, 'MMMM d, yyyy h:mm a')}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12}>
            <Pagination
              count={Math.ceil(filteredReportJobs.length / 5)}
              page={page}
              onChange={(_event, newPage) => setPage(newPage)}
              showFirstButton
              showLastButton
            />
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SelectReport;
